import React, { useState, useEffect, useRef } from 'react';
import { 
  MessageCircle, Send, Plus, Clock, CheckCircle, AlertCircle,
  ChevronLeft, RefreshCw, HelpCircle, FileText, CreditCard, Settings, MessageSquare
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const CATEGORIES = [
  { id: 'general', label: 'General Query', icon: HelpCircle },
  { id: 'technical', label: 'Technical Issue', icon: Settings },
  { id: 'billing', label: 'Billing/Payment', icon: CreditCard },
  { id: 'complaint', label: 'Complaint', icon: AlertCircle },
  { id: 'suggestion', label: 'Suggestion', icon: FileText }
];

const STATUS_CONFIG = {
  open: { color: 'bg-blue-500', text: 'Open', icon: Clock },
  in_progress: { color: 'bg-yellow-500', text: 'In Progress', icon: RefreshCw },
  resolved: { color: 'bg-green-500', text: 'Resolved', icon: CheckCircle },
  closed: { color: 'bg-gray-500', text: 'Closed', icon: CheckCircle }
};

const Helpdesk = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [newTicket, setNewTicket] = useState({ subject: '', message: '', category: 'general' });
  const [replyMessage, setReplyMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchTickets();
  }, []);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [selectedTicket?.messages]);

  const fetchTickets = async () => {
    try {
      const res = await api.get('/support/tickets');
      setTickets(res.data.tickets || []);
    } catch (error) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const createTicket = async () => {
    if (!newTicket.subject.trim() || !newTicket.message.trim()) {
      toast.error('Please fill all fields');
      return;
    }
    
    setSending(true);
    try {
      const res = await api.post('/support/tickets', newTicket);
      toast.success('Ticket created successfully!');
      setTickets([res.data.ticket, ...tickets]);
      setNewTicket({ subject: '', message: '', category: 'general' });
      setShowNewTicket(false);
      setSelectedTicket(res.data.ticket);
    } catch (error) {
      toast.error('Failed to create ticket');
    } finally {
      setSending(false);
    }
  };

  const sendReply = async () => {
    if (!replyMessage.trim()) return;
    
    setSending(true);
    try {
      await api.post(`/support/tickets/${selectedTicket.id}/reply?message=${encodeURIComponent(replyMessage)}`);
      
      // Update local state
      const updatedTicket = {
        ...selectedTicket,
        messages: [
          ...selectedTicket.messages,
          {
            sender_type: 'user',
            sender_name: 'You',
            message: replyMessage,
            timestamp: new Date().toISOString()
          }
        ]
      };
      setSelectedTicket(updatedTicket);
      setTickets(tickets.map(t => t.id === selectedTicket.id ? updatedTicket : t));
      setReplyMessage('');
      toast.success('Reply sent!');
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const refreshTicket = async () => {
    if (!selectedTicket) return;
    try {
      const res = await api.get(`/support/tickets/${selectedTicket.id}`);
      setSelectedTicket(res.data);
      setTickets(tickets.map(t => t.id === selectedTicket.id ? res.data : t));
    } catch (error) {
      toast.error('Failed to refresh');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-4 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-4" data-testid="helpdesk-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-green-500/20">
              <MessageCircle className="w-8 h-8 text-green-500" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Helpdesk</h1>
              <p className="text-sm text-muted-foreground">Free support - No AI, No Credits!</p>
            </div>
          </div>
          
          <button
            onClick={() => setShowNewTicket(true)}
            className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-all"
            data-testid="new-ticket-btn"
          >
            <Plus className="w-5 h-5" />
            New Ticket
          </button>
        </div>

        {/* Free Badge */}
        <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
          <p className="text-green-500 font-medium">
            This is a FREE feature! Send unlimited messages - our team will respond manually. No credits burned.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Ticket List */}
          <div className="lg:col-span-1 space-y-3">
            <h2 className="font-bold text-lg mb-4">Your Tickets ({tickets.length})</h2>
            
            {tickets.length === 0 ? (
              <div className="glass-card p-6 text-center">
                <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No tickets yet</p>
                <button
                  onClick={() => setShowNewTicket(true)}
                  className="mt-4 text-primary hover:underline"
                >
                  Create your first ticket
                </button>
              </div>
            ) : (
              tickets.map(ticket => {
                const statusConfig = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.open;
                const StatusIcon = statusConfig.icon;
                
                return (
                  <button
                    key={ticket.id}
                    onClick={() => setSelectedTicket(ticket)}
                    className={`w-full text-left glass-card p-4 hover:bg-white/5 transition-all ${
                      selectedTicket?.id === ticket.id ? 'ring-2 ring-primary' : ''
                    }`}
                    data-testid={`ticket-${ticket.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{ticket.subject}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(ticket.updated_at).toLocaleDateString()}
                        </p>
                      </div>
                      <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs text-white ${statusConfig.color}`}>
                        <StatusIcon className="w-3 h-3" />
                        {statusConfig.text}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                      {ticket.messages[ticket.messages.length - 1]?.message}
                    </p>
                  </button>
                );
              })
            )}
          </div>

          {/* Chat View */}
          <div className="lg:col-span-2">
            {selectedTicket ? (
              <div className="glass-card h-[600px] flex flex-col">
                {/* Chat Header */}
                <div className="p-4 border-b border-white/10 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setSelectedTicket(null)}
                      className="lg:hidden p-2 hover:bg-white/10 rounded-lg"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <div>
                      <h3 className="font-bold">{selectedTicket.subject}</h3>
                      <p className="text-xs text-muted-foreground capitalize">
                        {selectedTicket.category} • {STATUS_CONFIG[selectedTicket.status]?.text}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={refreshTicket}
                    className="p-2 hover:bg-white/10 rounded-lg"
                    title="Refresh"
                  >
                    <RefreshCw className="w-5 h-5" />
                  </button>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {selectedTicket.messages.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`flex ${msg.sender_type === 'admin' ? 'justify-start' : 'justify-end'}`}
                    >
                      <div className={`max-w-[80%] p-3 rounded-2xl ${
                        msg.sender_type === 'admin'
                          ? 'bg-blue-500/20 rounded-tl-none'
                          : 'bg-green-500/20 rounded-tr-none'
                      }`}>
                        <p className="text-xs font-medium mb-1">
                          {msg.sender_type === 'admin' ? 'Support Team' : 'You'}
                        </p>
                        <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(msg.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                {/* Reply Input */}
                {selectedTicket.status !== 'closed' && (
                  <div className="p-4 border-t border-white/10">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={replyMessage}
                        onChange={(e) => setReplyMessage(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && sendReply()}
                        placeholder="Type your message..."
                        className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                        disabled={sending}
                        data-testid="reply-input"
                      />
                      <button
                        onClick={sendReply}
                        disabled={sending || !replyMessage.trim()}
                        className="px-4 py-3 bg-green-500 text-white rounded-xl hover:bg-green-600 disabled:opacity-50 transition-all"
                        data-testid="send-reply-btn"
                      >
                        <Send className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                )}

                {selectedTicket.status === 'closed' && (
                  <div className="p-4 border-t border-white/10 text-center text-muted-foreground">
                    This ticket is closed. Create a new ticket for further assistance.
                  </div>
                )}
              </div>
            ) : (
              <div className="glass-card h-[600px] flex items-center justify-center">
                <div className="text-center">
                  <MessageCircle className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Select a ticket to view conversation</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* New Ticket Modal */}
        {showNewTicket && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="glass-card w-full max-w-lg p-6" data-testid="new-ticket-modal">
              <h2 className="text-xl font-bold mb-4">Create Support Ticket</h2>
              
              {/* Category Selection */}
              <div className="mb-4">
                <label className="text-sm text-muted-foreground mb-2 block">Category</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {CATEGORIES.map(cat => {
                    const Icon = cat.icon;
                    return (
                      <button
                        key={cat.id}
                        onClick={() => setNewTicket({ ...newTicket, category: cat.id })}
                        className={`p-3 rounded-xl border text-sm flex flex-col items-center gap-1 transition-all ${
                          newTicket.category === cat.id
                            ? 'border-green-500 bg-green-500/20'
                            : 'border-white/10 hover:border-white/30'
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                        {cat.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Subject */}
              <div className="mb-4">
                <label className="text-sm text-muted-foreground mb-2 block">Subject</label>
                <input
                  type="text"
                  value={newTicket.subject}
                  onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                  placeholder="Brief description of your issue"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="ticket-subject-input"
                />
              </div>

              {/* Message */}
              <div className="mb-6">
                <label className="text-sm text-muted-foreground mb-2 block">Message</label>
                <textarea
                  value={newTicket.message}
                  onChange={(e) => setNewTicket({ ...newTicket, message: e.target.value })}
                  placeholder="Describe your issue in detail..."
                  rows={5}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                  data-testid="ticket-message-input"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowNewTicket(false)}
                  className="flex-1 px-4 py-3 border border-white/20 rounded-xl hover:bg-white/5 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={createTicket}
                  disabled={sending}
                  className="flex-1 px-4 py-3 bg-green-500 text-white rounded-xl hover:bg-green-600 disabled:opacity-50 transition-all"
                  data-testid="submit-ticket-btn"
                >
                  {sending ? 'Creating...' : 'Create Ticket'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Helpdesk;
